import { FC } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ApprovedUrl } from "@models/approvedUrl.model";

export interface ApprovedUrlFormValues {
  url: string;
  title: string;
  description: string;
  is_active: boolean;
}

interface Props {
  show: boolean;
  approvedUrl: ApprovedUrl | null; // null = create
  /** Lowercased addresses of the org's other URLs, for instant duplicate feedback. */
  existingUrls: string[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (values: ApprovedUrlFormValues) => Promise<void>;
}

const ApprovedUrlFormModal: FC<Props> = ({ show, approvedUrl, existingUrls, isSaving, onClose, onSave }) => {
  const isEdit = approvedUrl != null;

  const validationSchema = Yup.object().shape({
    url: Yup.string()
      .trim()
      .required("Address is required")
      .url("Enter a full address, including https://")
      .max(2048, "Address must be 2048 characters or fewer")
      .test(
        "unique-per-org",
        "This organization already has an approved URL with that address",
        (value) => !value || !existingUrls.includes(value.trim().toLowerCase()),
      ),
    title: Yup.string().trim().max(255, "Name must be 255 characters or fewer"),
    description: Yup.string(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      url: approvedUrl?.url ?? "",
      title: approvedUrl?.title ?? "",
      description: approvedUrl?.description ?? "",
      is_active: approvedUrl?.is_active ?? true,
    } as ApprovedUrlFormValues,
    validationSchema,
    onSubmit: async (values: ApprovedUrlFormValues) => {
      try {
        await onSave({
          url: values.url.trim(),
          title: values.title.trim(),
          description: values.description.trim(),
          is_active: values.is_active,
        });
      } catch {
        // Server-side errors (e.g. duplicate 409) are surfaced via toast by the hook;
        // keep the modal open so the user can correct and retry.
      }
    },
  });

  return (
    <Modal show={show} onHide={onClose} centered backdrop={isSaving ? "static" : true}>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title as="h5">{isEdit ? "Edit approved URL" : "Add approved URL"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="approved-url-title">
            <Form.Label className="mb-1 fw-normal">Name</Form.Label>
            <Form.Control
              type="text"
              autoFocus
              {...formik.getFieldProps("title")}
              isInvalid={!!(formik.touched.title && formik.errors.title)}
              disabled={isSaving}
              placeholder="e.g. Apply Now"
            />
            <Form.Text className="text-muted">
              What Ops sees in the Page URL list. Defaults to the address.
            </Form.Text>
            <Form.Control.Feedback type="invalid">{formik.errors.title}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="approved-url-address">
            <Form.Label className="mb-1 fw-normal">Address</Form.Label>
            <Form.Control
              type="text"
              {...formik.getFieldProps("url")}
              isInvalid={!!(formik.touched.url && formik.errors.url)}
              disabled={isSaving}
              placeholder="https://example.edu/apply"
            />
            <Form.Control.Feedback type="invalid">{formik.errors.url}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="approved-url-description">
            <Form.Label className="mb-1 fw-normal">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              {...formik.getFieldProps("description")}
              disabled={isSaving}
              placeholder="Optional — why this destination is used"
            />
          </Form.Group>

          <Form.Group controlId="approved-url-active">
            <Form.Check
              type="switch"
              label="Active"
              checked={formik.values.is_active}
              onChange={(e) => formik.setFieldValue("is_active", e.target.checked)}
              disabled={isSaving}
            />
            <Form.Text className="text-muted">
              Inactive URLs stay on record but are not offered when building an ad.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving…" : isEdit ? "Save changes" : "Add URL"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ApprovedUrlFormModal;
